import React, { useState, useEffect, useCallback, FC } from 'react';
import { Table } from 'antd';
import type { GetProp, TableProps } from 'antd';
import { Icons } from '@assets';
import Highlighter from 'react-highlight-words';
import HeaderBooking from './ui/HeaderBooking/HeaderBooking';
import { IBookingObj, IPaginatedProps } from '@types';
import ReservationModal from '../components/ReservationModal';
import DialogModal from '../components/DialogModal';
import { useDispatch, useSelector } from 'react-redux';
import { authInfoSelect } from '@entities/auth';
import {
    cssOverride,
    limitBooking,
    limitCardChanges,
    limitCardTrans,
    optionsBookingActionsSelect,
} from '@utils/const';
import { bookingSelect } from '@entities/booking';
import { AppDispatch } from '@config/store';
import { getBookins } from '@api/thunks/booking';
import { PuffLoader } from 'react-spinners';
import { IBookingQuery } from '@api/services/bookingApi/types';
import { cardSelect } from '@entities/card';
import ClientModal from '@pages/crm/components/ClientModal';
import * as S from './styles';
import { getBookingColumns } from '@pages/booking/booking/ui/columns/bookingColumns';
import { colors } from '@styles';
import { useClientActions } from '@hooks/useClientActions';
import { useClientData } from '@hooks/useClientData';
import { useBookingWebSocket } from '@hooks/useBookingWebSocket';

type TablePaginationConfig = Exclude<
    GetProp<TableProps, 'pagination'>,
    boolean
>;

interface TableParams {
    pagination?: TablePaginationConfig;
    sortField?: string;
    sortOrder?: string;
    filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

const BookingPage: FC = () => {
    const { shop } = useSelector(authInfoSelect);
    const { bookings, total } = useSelector(bookingSelect);
    const [openedPopup, setOpenedPopup] = useState(false);
    const [activeModal, setActiveModal] = useState<string>('settings');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [loading, setLoading] = useState(false);
    const [openedSettingsPopup, setOpenedSettingsPopup] =
        useState<boolean>(false);
    // const [query, setQuery] = useState<            TO DO: Доработать
    //     'all' | 'future' | 'declined' | 'complete'
    // >('future');
    const [selectedRecord, setSelectedRecord] = useState<
        IBookingObj | undefined
    >(undefined);
    const [searchText, setSearchText] = useState('');
    const [valueBulkSelector, setValueBulkSelector] = useState<
        string | undefined
    >(undefined);

    const [openedDialog, setOpenedDialog] = useState<boolean>(false);
    const [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: limitBooking,
        },
    });
    const [changesPagination, setChangesPagination] = useState<IPaginatedProps>(
        {
            page: 1,
            limit: limitCardChanges,
        }
    );
    const [transPagination, setTransPagination] = useState<IPaginatedProps>({
        page: 1,
        limit: limitCardTrans,
    });
    const { transactionCount, changesCount } = useSelector(cardSelect);
    const dispatch = useDispatch<AppDispatch>();

    const getData = useCallback(() => {
        if (shop) {
            const { current, pageSize } = tableParams.pagination!;
            const request: IBookingQuery = {
                limit: pageSize!,
                offset: (current! - 1) * pageSize!,
                type: 'all', //query,
            };

            dispatch(getBookins({ shop_id: shop?.id, params: request }));
        }
    }, [shop, tableParams.pagination, dispatch]);

    useBookingWebSocket({
        onDataUpdate: getData,
    });

    // const onChange = (e: RadioChangeEvent) => {
    //     setQuery(e.target.value);
    //     setLoading(true);
    // };

    const getColumnSearchProps = () => ({
        render: (text: string) =>
            searchText ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text.toString()}
                />
            ) : (
                text
            ),
    });

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const modalVisibilityToggler = async () => {
        setOpenedPopup(false);
        setOpenedDialog(false);
    };

    const onChangeBulkSelector = (value: any) => {
        setValueBulkSelector(value);
    };

    const bulkHandler = () => {
        setOpenedDialog(true);
    };

    const handleTableChange: TableProps['onChange'] = (
        pagination,
        filters,
        sorter
    ) => {
        setTableParams({
            pagination,
            filters,
            ...sorter,
        });
        setLoading(true);
    };

    const handleRowClick = (record: IBookingObj) => {
        if (record) {
            setActiveModal('settings');
            setSelectedRecord(record);
            setOpenedPopup(true);
        }
    };

    const handleNew = () => {
        setActiveModal('new');
        setOpenedPopup(true);
    };

    const { handleShowClientModal, handlerClickId } = useClientActions({
        transPagination,
        changesPagination,
        setOpenedSettingsPopup,
    });
    useClientData(transPagination, changesPagination);

    const loadMore = () => {
        if (transactionCount > transPagination.page * transPagination.limit) {
            const newPage = transPagination.page + 1;
            setTransPagination(prev => ({
                ...prev,
                page: newPage,
            }));
        }
    };

    const loadMoreCardChanges = () => {
        if (changesCount > changesPagination.page * changesPagination.limit) {
            const newPage = changesPagination.page + 1;

            setChangesPagination(prev => ({
                ...prev,
                page: newPage,
            }));
        }
    };

    useEffect(() => {
        if (loading && shop) {
            getData();
            setLoading(false);
        }
    }, [loading, tableParams, getData, shop]);

    useEffect(() => {
        setLoading(true);
    }, []);

    const columns = getBookingColumns({
        handleRowClick,
        handlerClickId,
        getColumnSearchProps,
        searchText,
    });

    const renderTableFooter = () => {
        return (
            <S.FooterWrap>
                <S.ActionWrap>
                    <S.StyledSelect
                        options={optionsBookingActionsSelect}
                        onChange={v => onChangeBulkSelector(v)}
                    />
                    <S.StyledActionButton onClick={bulkHandler}>
                        <span>Применить</span>
                    </S.StyledActionButton>
                </S.ActionWrap>
            </S.FooterWrap>
        );
    };

    if (loading) {
        return <PuffLoader color={colors.loader} cssOverride={cssOverride} />;
    }

    return (
        <S.Wrapper>
            <S.Header>
                <S.HeaderTitle>Бронирование</S.HeaderTitle>
            </S.Header>
            <HeaderBooking data={bookings} />
            <S.SearchWrap>
                <S.SearchInput
                    placeholder="Поиск..."
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                />

                <S.ButtonBlock>
                    <S.StyledAddButton onClick={handleNew}>
                        <img src={Icons.AddGreyPlusInCircle} alt="icon grey" />
                        <span>Добавить</span>
                    </S.StyledAddButton>
                    <S.StyledButton>
                        <img src={Icons.IconSearchVect1} alt="icon search" />
                    </S.StyledButton>
                </S.ButtonBlock>
            </S.SearchWrap>
            {/*<S.TabsWrapper>*/}
            {/*    <Radio.Group*/}
            {/*        value={query}*/}
            {/*        onChange={onChange}*/}
            {/*        style={{ marginBottom: 16 }}*/}
            {/*    >*/}
            {/*        <Radio.Button value="future">Предстоящие</Radio.Button>*/}
            {/*        <Radio.Button value="complete">Завершенные</Radio.Button>*/}
            {/*        <Radio.Button value="declined">Отклоненные</Radio.Button>*/}
            {/*        <Radio.Button value="all">Все</Radio.Button>*/}
            {/*    </Radio.Group>*/}
            {/*</S.TabsWrapper>*/}
            <S.TableWrapper>
                <Table
                    size="small"
                    scroll={{ x: 1200 }}
                    rowSelection={rowSelection}
                    columns={columns}
                    rowKey={rec => rec.id}
                    dataSource={bookings}
                    onChange={handleTableChange}
                    pagination={{
                        current: tableParams.pagination?.current,
                        pageSize: tableParams.pagination?.pageSize,
                        total,
                    }}
                    footer={renderTableFooter}
                    getPopupContainer={trigger => trigger.parentElement!}
                />
            </S.TableWrapper>
            <S.Overlay
                display={
                    openedPopup || openedDialog || openedSettingsPopup
                        ? 'block'
                        : 'none'
                }
            />
            {openedPopup && (
                <ReservationModal
                    modalVisibilityToggler={modalVisibilityToggler}
                    activeModal={activeModal}
                    record={selectedRecord}
                />
            )}
            {openedDialog && valueBulkSelector && (
                <DialogModal
                    modalVisibilityToggler={modalVisibilityToggler}
                    data={bookings}
                    activeDialog={valueBulkSelector}
                    selectedIds={selectedRowKeys}
                />
            )}
            {openedSettingsPopup && (
                <ClientModal
                    loadMoreCardChanges={loadMoreCardChanges}
                    loadMore={loadMore}
                    setOpeningSettingsPopup={handleShowClientModal}
                />
            )}
        </S.Wrapper>
    );
};

export default BookingPage;
